<template>
  <Teleport to="body">
    <Transition name="ui-modal-backdrop" appear>
      <div
        v-if="modelValue"
        class="ui-modal__backdrop"
        :style="{ zIndex: backdropZIndex }"
        @mousedown.self="onBackdropClick"
      />
    </Transition>
    <Transition
      name="ui-modal-content"
      appear
      @after-enter="onAfterEnter"
      @after-leave="onAfterLeave"
    >
      <div
        v-if="modelValue"
        class="ui-modal__container"
        :style="{ zIndex: contentZIndex }"
        :data-print-title="printTitle || undefined"
        @mousedown.self="onBackdropClick"
      >
        <div
          ref="dialogRef"
          :class="['ui-modal__dialog', sizeClass]"
          role="dialog"
          aria-modal="true"
          :aria-labelledby="ariaLabelledBy"
          :id="id"
          tabindex="-1"
          @keydown="onKeydown"
        >
          <header v-if="!hideHeader" class="ui-modal__header">
            <h5
              v-if="title || $slots.title"
              :id="titleId"
              class="ui-modal__title"
            >
              <slot name="title">
                {{ title }}
              </slot>
            </h5>
            <button
              type="button"
              class="ui-modal__close"
              aria-label="Close"
              @click="cancel"
            >
              <svg
                viewBox="0 0 24 24"
                fill="none"
                width="20"
                height="20"
                aria-hidden="true"
              >
                <path
                  stroke="currentColor"
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  stroke-width="2"
                  d="M6 6l12 12M6 18L18 6"
                />
              </svg>
            </button>
          </header>

          <div class="ui-modal__body">
            <slot />
          </div>

          <footer v-if="!hideFooter" class="ui-modal__footer">
            <slot name="footer">
              <UiButton
                v-if="!okOnly"
                :variant="cancelVariant"
                :disabled="busy || cancelDisabled"
                @click="cancel"
              >
                {{ cancelTitle }}
              </UiButton>
              <UiButton
                :variant="okVariant"
                :disabled="busy || okDisabled"
                @click="ok"
              >
                {{ okTitle }}
              </UiButton>
            </slot>
          </footer>
        </div>
      </div>
    </Transition>
  </Teleport>
</template>

<script setup lang="ts">
  /**
   * UiModal — Tailwind primitive replacing BootstrapVue-Next's `b-modal`.
   *
   * In-house rebuild rather than a wrapper — owns the focus-trap, body
   * scroll lock, ARIA wiring, animation, and stacking layer end-to-end.
   * Eliminates the `_custom.scss` `.modal-footer .btn-*` shim that was
   * previously required to give BVN's modal MK3 chrome.
   *
   * API matches `b-modal` closely so call-site migrations are mechanical:
   *
   *   <UiModal v-model="visible" title="Edit User" @ok="onOk" @hidden="reset">
   *     <UiInput v-model="form.name" />
   *   </UiModal>
   *
   * Migration notes for future maintainers:
   * - BVN's `static` / `lazy` props are accepted but no-ops. UiModal's
   *   render is conditional on `modelValue`, which already gives "lazy"
   *   semantics, and the Teleport target is always `body`, which gives
   *   "static" semantics. They're declared so existing call sites don't
   *   produce "extraneous attribute" warnings during migration.
   * - The BVN `v-b-modal.<id>` directive isn't supported. Migration sites
   *   replace those triggers with explicit `@click="visible = true"` on
   *   the trigger element + a `ref` for the visibility state.
   *
   * Stacking: nested modals (a modal opening over another modal) use a
   * module-level counter to bump z-index 10 per level so the new modal's
   * backdrop sits above the previous one's content. Tested up to 3
   * levels deep; deeper nesting works but is UX-questionable.
   *
   * Focus trap: when open, Tab/Shift+Tab cycles focus inside the modal.
   * Pre-open focus is captured and restored on close.
   */
  import { ref, computed, onBeforeUnmount, watch, nextTick, useSlots } from 'vue'
  import UiButton from './UiButton.vue'

  // Module-level open-modal stack — shared across all UiModal instances.
  // Used to drive nested-modal z-index spacing and "only unlock body
  // scroll when the last modal closes" behaviour. Lives outside the
  // component so refs to other instances coordinate naturally.
  const openModals: symbol[] = []

  // Module-level id counter — `<script setup>` re-runs per instance, so
  // an in-setup `let _idCounter = 0` would always increment to 1 and
  // every modal would share `ui-modal-title-1`. Lifting the counter to
  // module scope gives each modal a unique id for aria-labelledby.
  let _idCounter = 0

  defineOptions({ name: 'UiModal', inheritAttrs: false })

  type ButtonVariant = 'primary' | 'secondary' | 'success' | 'danger' | 'warning' | 'info' | 'link' | 'ghost'

  interface Props {
    modelValue?: boolean
    title?: string
    size?: 'sm' | 'md' | 'lg' | 'xl' | 'full'
    okOnly?: boolean
    okTitle?: string
    cancelTitle?: string
    okVariant?: ButtonVariant
    cancelVariant?: ButtonVariant
    okDisabled?: boolean
    cancelDisabled?: boolean
    hideHeader?: boolean
    hideFooter?: boolean
    busy?: boolean
    noCloseOnEsc?: boolean
    noCloseOnBackdrop?: boolean
    id?: string
    /** BVN parity, no-op — UiModal already lazy-renders on modelValue. */
    lazy?: boolean
    /** BVN parity, no-op — UiModal always teleports to body. */
    static?: boolean
    /** BVN parity, alias for `size="lg"` (BVN supports it as a boolean prop). */
    lg?: boolean
    /** BVN parity, alias for `size="sm"`. */
    sm?: boolean
    /**
     * Override label for the print header when a PagedTable inside
     * this modal is printed. Use when the modal's visible `title`
     * is a generic "All X" but the print should read with the
     * source context (e.g. modal title="All Business Units" but
     * print-title="Top Performers - Business Units (Cost Centres)").
     * Falls back to `title` if not set. See utils/printContext.ts.
     */
    printTitle?: string
  }

  const props = withDefaults(defineProps<Props>(), {
    modelValue: false,
    title: '',
    size: 'md',
    okOnly: false,
    okTitle: 'OK',
    cancelTitle: 'Cancel',
    okVariant: 'primary',
    cancelVariant: 'secondary',
    okDisabled: false,
    cancelDisabled: false,
    hideHeader: false,
    hideFooter: false,
    busy: false,
    noCloseOnEsc: false,
    noCloseOnBackdrop: false,
    id: undefined,
    lazy: false,
    static: false,
    lg: false,
    sm: false,
    printTitle: '',
  })

  // BVN-style cancellable event payload — call sites can `evt.preventDefault()`
  // inside @ok / @cancel / @hide handlers to keep the modal open after the
  // event fires (e.g. validating a form before closing).
  interface ModalEvent { preventDefault: () => void; defaultPrevented: boolean }
  function makeEvent (): ModalEvent {
    const evt: ModalEvent = {
      defaultPrevented: false,
      preventDefault () { evt.defaultPrevented = true },
    }
    return evt
  }

  const emit = defineEmits<{
    'update:modelValue': [value: boolean]
    'show': []
    'shown': []
    'hide': [event: ModalEvent]
    'hidden': []
    'ok': [event: ModalEvent]
    'cancel': [event: ModalEvent]
  }>()

  // Auto-incrementing instance id for the title's `aria-labelledby` wiring.
  // Counter declared at module scope above.
  const titleId = `ui-modal-title-${++_idCounter}`

  const instanceId = Symbol('ui-modal')

  // aria-labelledby points at the title element when there's a title to
  // label by. The boolean coercion is split out because the equivalent
  // inline expression `title || $slots.title ? titleId : undefined` ran
  // into operator-precedence subtleties in Vue's template compiler.
  const slots = useSlots()
  const ariaLabelledBy = computed(() => {
    if (props.title) return titleId
    if (slots.title) return titleId
    return undefined
  })

  const dialogRef = ref<HTMLElement | null>(null)
  const previouslyFocused = ref<HTMLElement | null>(null)

  // sm-from-prop wins over the size enum when both are set, matching BVN.
  const sizeClass = computed(() => {
    if (props.lg) return 'ui-modal__dialog--lg'
    if (props.sm) return 'ui-modal__dialog--sm'
    return `ui-modal__dialog--${props.size}`
  })

  // Stack depth → z-index. 1040/1050 base (matches Bootstrap), +10 per level.
  const stackDepth = computed(() => Math.max(0, openModals.indexOf(instanceId)))
  const backdropZIndex = computed(() => 1040 + stackDepth.value * 10)
  const contentZIndex = computed(() => 1050 + stackDepth.value * 10)

  function focusableElements (): HTMLElement[] {
    const dialog = dialogRef.value
    if (!dialog) return []
    return Array.from(dialog.querySelectorAll<HTMLElement>(
      'button:not([disabled]), [href], input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])'
    )).filter(el => !el.hidden && el.offsetParent !== null)
  }

  function onKeydown (ev: KeyboardEvent) {
    if (ev.key === 'Escape' && !props.noCloseOnEsc) {
      ev.stopPropagation()
      cancel()
      return
    }
    if (ev.key !== 'Tab') return
    const els = focusableElements()
    if (!els.length) {
      ev.preventDefault()
      dialogRef.value?.focus()
      return
    }
    const first = els[0]
    const last = els[els.length - 1]
    const active = document.activeElement
    if (ev.shiftKey && (active === first || active === dialogRef.value)) {
      ev.preventDefault()
      last.focus()
    } else if (!ev.shiftKey && active === last) {
      ev.preventDefault()
      first.focus()
    }
  }

  function onBackdropClick () {
    if (props.noCloseOnBackdrop) return
    cancel()
  }

  function ok () {
    const evt = makeEvent()
    emit('ok', evt)
    if (!evt.defaultPrevented) close()
  }

  function cancel () {
    const evt = makeEvent()
    emit('cancel', evt)
    if (!evt.defaultPrevented) close()
  }

  function close () {
    const evt = makeEvent()
    emit('hide', evt)
    if (evt.defaultPrevented) return
    emit('update:modelValue', false)
  }

  function onAfterEnter () {
    emit('shown')
  }

  function onAfterLeave () {
    emit('hidden')
  }

  // Open / close lifecycle — runs once per `modelValue` flip. Manages the
  // body-scroll lock, the focus capture+restore, and the modal stack.
  watch(() => props.modelValue, async (open, prev) => {
    if (open === prev) return
    if (open) {
      previouslyFocused.value = document.activeElement as HTMLElement | null
      openModals.push(instanceId)
      if (openModals.length === 1) {
        document.body.style.overflow = 'hidden'
      }
      emit('show')
      await nextTick()
      // Land focus on the first focusable element, falling back to the
      // dialog itself so screen readers know the dialog opened.
      const first = focusableElements()[0]
      ;(first ?? dialogRef.value)?.focus()
    } else {
      const i = openModals.indexOf(instanceId)
      if (i >= 0) openModals.splice(i, 1)
      if (openModals.length === 0) {
        document.body.style.overflow = ''
      }
      previouslyFocused.value?.focus?.()
      previouslyFocused.value = null
    }
  }, { immediate: false })

  // If the component unmounts while the modal is open (e.g. v-if on a
  // parent route guard), make sure we don't leak the body scroll lock or
  // a stale stack entry.
  onBeforeUnmount(() => {
    const i = openModals.indexOf(instanceId)
    if (i >= 0) openModals.splice(i, 1)
    if (openModals.length === 0) document.body.style.overflow = ''
  })

  defineExpose({
    /** Imperative open/close — useful for parent components that prefer
     *  ref-based control over v-model. */
    show: () => emit('update:modelValue', true),
    hide: () => emit('update:modelValue', false),
  })
</script>

<style scoped>
  /* Backdrop — fixed, full-viewport, semi-transparent dim layer. The
     z-index is set inline (driven by stack depth) so nested modals
     can stack correctly. */
  .ui-modal__backdrop {
    position: fixed;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background-color: rgba(0, 0, 0, 0.5);
  }

  /* Container — full-viewport flex centre. Catches click-outside on the
     padded area around the dialog so caller can configure
     `:no-close-on-backdrop`. */
  .ui-modal__container {
    position: fixed;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    display: flex;
    align-items: flex-start;
    justify-content: center;
    padding: 1.75rem 1rem;
    overflow-y: auto;
    /* Prevent the container itself from intercepting non-self clicks
       — only `@mousedown.self` triggers backdrop close. */
    pointer-events: auto;
  }

  /* Dialog — the visible modal panel. White card with rounded corners,
     subtle shadow, and width tiers tuned for a data-heavy app. The xl
     and full tiers use viewport-relative caps so they don't overflow
     on smaller screens; sm/md/lg stay fixed because their use cases
     (confirms, simple forms) don't need more than the cap on any
     reasonable screen. */
  .ui-modal__dialog {
    background-color: #fff;
    border-radius: 0.5rem;
    box-shadow: 0 20px 50px rgba(0, 0, 0, 0.2);
    width: 100%;
    max-width: 640px;          /* md default */
    margin: auto;
    display: flex;
    flex-direction: column;
    max-height: calc(100vh - 3.5rem);
    outline: none;
  }
  .ui-modal__dialog--sm   { max-width: 360px; }
  .ui-modal__dialog--md   { max-width: 640px; }
  .ui-modal__dialog--lg   { max-width: 960px; }
  .ui-modal__dialog--xl   { max-width: min(1280px, 92vw); }
  .ui-modal__dialog--full { max-width: min(1600px, 95vw); }

  /* Header — flex row, title left, close × right. Border-bottom matches
     UiCard's chrome convention. */
  .ui-modal__header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 1rem 1.25rem;
    border-bottom: 1px solid #e5e7eb;
  }
  .ui-modal__title {
    margin: 0;
    font-size: 1.125rem;
    font-weight: 500;
    color: #111827;
    line-height: 1.5;
  }
  .ui-modal__close {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 2rem;
    height: 2rem;
    padding: 0;
    /* Subtle always-on gray-200 border so the dismiss target is
       discoverable on every modal, not just when keyboard-focused.
       The blue focus ring still sits on top via :focus-visible
       below. */
    border: 1px solid #e5e7eb;
    background: transparent;
    color: #6b7280;
    cursor: pointer;
    border-radius: 0.25rem;
    transition:
      background-color 150ms ease,
      border-color    150ms ease,
      color           150ms ease;
  }
  .ui-modal__close:hover {
    background-color: #f3f4f6;
    border-color: #d1d5db;        /* gray-300 — slightly darker on hover */
    color: #111827;
  }
  .ui-modal__close:focus-visible {
    outline: none;
    box-shadow: 0 0 0 3px rgba(37, 99, 235, 0.25);
  }

  /* Body — scrollable content region. Padding mirrors header for visual
     symmetry; `overflow-y: auto` lets long forms scroll inside the modal
     rather than blowing out its height. */
  .ui-modal__body {
    padding: 1.25rem;
    overflow-y: auto;
    flex: 1 1 auto;
  }

  /* Footer — right-aligned button row with default Cancel / OK. The
     CSS shim `_custom.scss .modal-footer .btn-*` is no longer needed
     since UiButton renders MK3 chrome by construction. */
  .ui-modal__footer {
    display: flex;
    justify-content: flex-end;
    gap: 0.5rem;
    padding: 0.75rem 1.25rem;
    border-top: 1px solid #e5e7eb;
  }

  /* Transitions — backdrop fades in/out, content fades + lifts slightly. */
  .ui-modal-backdrop-enter-active,
  .ui-modal-backdrop-leave-active {
    transition: opacity 150ms ease;
  }
  .ui-modal-backdrop-enter-from,
  .ui-modal-backdrop-leave-to {
    opacity: 0;
  }
  .ui-modal-content-enter-active,
  .ui-modal-content-leave-active {
    transition: opacity 150ms ease, transform 150ms ease;
  }
  .ui-modal-content-enter-from,
  .ui-modal-content-leave-to {
    opacity: 0;
    transform: translateY(-12px);
  }
</style>
